import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./Tooltip.module.css";

export enum TooltipPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
}

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: TooltipPosition;
  gap?: number;
}

export const Tooltip = (props: TooltipProps) => {
  const { content, children, position = TooltipPosition.TOP, gap = 10 } = props;

  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) {
      return;
    }

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case TooltipPosition.TOP:
        top = trigger.top - tooltip.height - gap;
        left = trigger.left + trigger.width / 2 - tooltip.width / 2;
        break;
      case TooltipPosition.BOTTOM:
        top = trigger.bottom + gap;
        left = trigger.left + trigger.width / 2 - tooltip.width / 2;
        break;
      case TooltipPosition.LEFT:
        top = trigger.top + trigger.height / 2 - tooltip.height / 2;
        left = trigger.left - tooltip.width - gap;
        break;
      case TooltipPosition.RIGHT:
        top = trigger.top + trigger.height / 2 - tooltip.height / 2;
        left = trigger.right + gap;
        break;
    }

    const margin = 5;
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltip.width - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - tooltip.height - margin));

    setCoords({ top, left });
  }, [isVisible, position, gap]);

  return (
    <div
      ref={triggerRef}
      className={styles.tooltipContainer}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={UStyles.classNames(styles.tooltip, styles[position])}
            style={{ top: coords.top, left: coords.left }}
          >
            {content}
            <div className={styles.arrow} />
          </div>,
          document.body,
        )}
    </div>
  );
};
